import React, { useEffect, useState, useContext, useRef } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { S3_URL_PRODUCTS } from "../utils/constants";
import {
  getProductById,
  getProductsByListOfIds,
} from "../services/nonAuthRequets";
import { AuthContext } from "../contexts/AuthContext";
import { toast } from "react-toastify";
import {
  getCoupondetails,
  getAvailableCoupons,
} from "../services/authenticatedRequests";
import LoadingComponent from "./LoadingComponent";
import NoResultsComponent from "./NoResultsComponent";
const DELIVERY_FEE = 400;
const CartSection = () => {
  document.title = "Cart | Bigmall";
  const { cart, removeFromCart, clearCartItems, updateQuantity, isCartLoaded } =
    useCart();
  const [products, setProducts] = useState([]);
  const { isAuthenticated } = useContext(AuthContext);
  const [couponDetails, setCouponDetails] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  // Ref to hold debouncing timers for each product
  const updateTimeouts = useRef({});

  function qualifiesForFreeDelivery(product) {
    // Check if the configuration exists and has a "Free_Delivery" key.
    if (product.configuration && product.configuration.Free_Delivery) {
      const freeDeliveryConfig = product.configuration.Free_Delivery;
      return (
        freeDeliveryConfig.configStatus &&
        freeDeliveryConfig.configStatus.toLowerCase() === "active"
      );
    }
    // If no free delivery configuration exists, assume the product does not qualify.
    return false;
  }

  const fetchAvailableCoupons = async () => {
    try {
      const data = await getAvailableCoupons();
      setAvailableCoupons(data);
    } catch (error) {
      console.error("Error fetching available coupons:", error);
    }
  };

  useEffect(() => {
    // Fetch detailed product data for items in the cart.
    const fetchProductsData = async (cartItems, isAuth) => {
      // Extract all product IDs from the cart.
      const productIds = cartItems.map((item) =>
        isAuth ? item.id.productId : item.id
      );
      // console.log("ProductIds", productIds);
      // Call the API once with the list of IDs.
      const response = await getProductsByListOfIds(productIds);
      // console.log("Response", response);
      // Assume the API returns an array of product details in response.products or directly as the response.
      const productsFromApi = Array.isArray(response)
        ? response
        : response.products;
      //console.log("ProductsFromApi", productsFromApi);
      if (!productsFromApi || productsFromApi.length === 0) {
        return [];
      }

      // For each cart item, find its corresponding product detail.
      const productData = cartItems.map((item) => {
        const prodId = isAuth ? item.id.productId : item.id;
        //console.log("ProdId", prodId);
        const productWrapper = productsFromApi.find(
          (p) => p.product.id === prodId
        );
        //console.log("Product", productWrapper);
        if (!productWrapper) return null;
        const product = productWrapper.product;
        return {
          id: product.id,
          name: product.name,
          images: product.images,
          salePrice: product.salePrice,
          regularPrice: product.regularPrice,
          stockQuantity: product.stockQuantity,
          quantity: item.quantity,
          sku: product.sku,
          configuration: product.configuration || null,
        };
      });
      // Filter out any null values (in case a product wasn't found).
      return productData.filter((p) => p !== null);
    };

    const fetchProducts = async () => {
      setLoading(true);
      if (cart.length === 0) {
        setLoading(false);
        setProducts([]);
        return;
      }
      try {
        const productsData = await fetchProductsData(cart, isAuthenticated);
        // console.log("ProductsData", productsData);
        setProducts(productsData);
      } catch (error) {
        console.error("Error fetching product data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
    if (localStorage.getItem("couponDetails")) {
      setCouponDetails(JSON.parse(localStorage.getItem("couponDetails")));
    }
  }, [cart, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && cart.length > 0) {
      fetchAvailableCoupons();
    }
  }, [isAuthenticated, cart]);

  const allFreeDelivery = products.every(qualifiesForFreeDelivery);
  const deliveryFee = allFreeDelivery ? 0 : DELIVERY_FEE;
  const cartTotal = products
    .reduce((total, item) => {
      const config = item.configuration;
      const configDiscount =
        config?.Sale?.collectionConfiguration?.discount || 0;
      const discountActive =
        config && configDiscount && config.Sale.configStatus === "active";

      let effectivePrice;
      if (discountActive) {
        if (item.salePrice) {
          // Case 1: Active config and sale price available
          effectivePrice = item.salePrice * (1 - configDiscount / 100);
        } else {
          // Case 3: Active config but no sale price, use regular price
          effectivePrice = item.regularPrice * (1 - configDiscount / 100);
        }
      } else {
        // Case 2 & 4: No active config; use sale price if available, otherwise regular price
        effectivePrice = item.salePrice || item.regularPrice;
      }

      return total + item.quantity * effectivePrice;
    }, 0)
    .toFixed(2);

  // Debounced handler to update the quantity with an optimistic UI update.
  const debouncedHandleUpdateQuantity = (productId, newQuantity) => {
    // Update the local products state immediately.
    setProducts((prevProducts) =>
      prevProducts.map((product) =>
        product.id === productId
          ? { ...product, quantity: newQuantity }
          : product
      )
    );

    // Clear any previous timeout for this product.
    if (updateTimeouts.current[productId]) {
      clearTimeout(updateTimeouts.current[productId]);
    }

    // Debounce the update call by 500ms.
    updateTimeouts.current[productId] = setTimeout(async () => {
      try {
        // Using the unified updateQuantity from the cart context
        await updateQuantity(productId, newQuantity);
        toast.success("Cart updated successfully");
      } catch (error) {
        console.error("Error updating cart:", error);
        toast.error("Failed to update cart");
      }
    }, 500);
  };

  // Remove a single item from the cart.
  const handleRemoveFromCart = async (productId) => {
    try {
      await removeFromCart(productId);
      toast.success("Item removed from cart");
    } catch (error) {
      console.error("Error removing item from cart:", error);
      toast.error("Failed to remove item from cart");
    }
  };

  // Clear the entire cart.
  const handleDeleteCart = async () => {
    if (!window.confirm("Are you sure you want to clear your cart?")) {
      return; // Exit if the user cancels
    }
    try {
      await clearCartItems();
      localStorage.removeItem("couponDetails");
      setCouponDetails(null);
      setAvailableCoupons([]);
      toast.success("Cart deleted successfully");
    } catch (error) {
      console.error("Error deleting cart:", error);
      toast.error("Failed to delete cart");
    }
  };

  const handleInputChange = (e) => {
    setCouponCode(e.target.value);
  };

  const handleCouponSubmit = async (e) => {
    e.preventDefault();
    //console.log("CouponCode", couponCode);
    try {
      const data = await getCoupondetails(couponCode);
      setCouponDetails(data);
      localStorage.setItem("couponDetails", JSON.stringify(data));
      //console.log("CouponDetails", data);
      toast.success("Coupon applied successfully");
    } catch (error) {
      console.error("Error applying coupon:", error);
      const errorMessage = error.response.data.error;
      toast.error(errorMessage || "Failed to apply coupon");
    }
  };

  const handleRemoveCoupon = async (e) => {
    e.preventDefault();
    setCouponDetails(null);
    setCouponCode("");
    localStorage.removeItem("couponDetails");
  };

  let discountAmount = 0;
  let discountedTotal = Number(cartTotal) || 0;
  if (couponDetails) {
    if (couponDetails.couponType === "PERCENTAGE") {
      discountAmount = (discountedTotal * couponDetails.discountedValue) / 100;
    } else {
      discountAmount = Number(couponDetails.discountedValue) || 0;
    }
    discountedTotal -= discountAmount;
  }

  // Calculate the final total including delivery fee
  const finalTotal = discountedTotal + deliveryFee;

  if (loading) {
    return <LoadingComponent message="Loading cart…" />;
  }
  if (cart.length === 0) {
    return (
      <NoResultsComponent message="Your cart is empty. Start shopping now..." />
    );
  }

  
  // console.log("Products", products);
  // console.log("AvailableCoupons", availableCoupons);
  return (
    <section className="cart py-20 pb-40">
      <div className="container container-lg">
        <div className="row gy-4">
          <div className="col-xl-9 col-lg-8">
            <div className="cart-table border border-gray-100 rounded-8 py-10 px-10">
              <div className="overflow-x-auto scroll-sm scroll-sm-horizontal">
                <table className="table style-two">
                  <thead>
                    <tr>
                      <th className="h6 text-sm fw-bold">Product</th>
                      <th className="h6 text-sm fw-bold">Price</th>
                      <th className="h6 text-sm fw-bold">Quantity</th>
                      <th className="h6 text-sm fw-bold">Subtotal</th>
                      <th className="h6 text-sm fw-bold">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.length > 0 ? (
                      products.map((item) => {
                        // Extract configuration from the item
                        const config = item.configuration;
                        const configDiscount =
                          config?.Sale?.collectionConfiguration?.discount || 0;
                        const discountActive =
                          config &&
                          configDiscount &&
                          config.Sale.configStatus === "active";

                        let effectivePrice;
                        if (discountActive) {
                          if (item.salePrice) {
                            // Case 1: Active config and sale price available
                            effectivePrice =
                              item.salePrice * (1 - configDiscount / 100);
                          } else {
                            // Case 3: Active config but no sale price; calculate from regular price
                            effectivePrice =
                              item.regularPrice * (1 - configDiscount / 100);
                          }
                        } else {
                          // Case 2 & 4: No active config; use sale price if available or regular price
                          effectivePrice = item.salePrice || item.regularPrice;
                        }

                        return (
                          <tr key={item.id}>
                            <td>
                              <div className="table-product d-flex align-items-center">
                                <Link
                                  to={`/product-details/${item.id}`}
                                  className="table-product__thumb flex-center"
                                >
                                  <img
                                    src={`${S3_URL_PRODUCTS}${item.images[0]}`}
                                    alt={item.name}
                                  />
                                </Link>
                                <div className="table-product__content text-start">
                                  <h6 className="title text-sm fw-semibold mb-8">
                                    <Link
                                      to={`/product-details/${item.id}`}
                                      className="link text-line-2"
                                    >
                                      {item.name.length > 50
                                        ? `${item.name.slice(0, 50)}...`
                                        : item.name}
                                    </Link>
                                  </h6>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="text-sm mb-0 fw-semibold">
                                LKR {effectivePrice.toFixed(2)}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex rounded-4 overflow-hidden">
                                <button
                                  type="button"
                                  className="quantity__minus border border-end border-gray-100 flex-shrink-0 h-32 w-32 text-neutral-600 flex-center bg-gray-100 hover-bg-main-600 hover-text-white"
                                  onClick={() =>
                                    debouncedHandleUpdateQuantity(
                                      item.id,
                                      item.quantity - 1
                                    )
                                  }
                                  disabled={item.quantity <= 1}
                                >
                                  <i className="ph ph-minus" />
                                </button>
                                <input
                                  type="number"
                                  className="quantity__input flex-grow-1 border border-gray-100 border-start-0 border-end-0 text-center w-32 px-4"
                                  value={item.quantity}
                                  onChange={(e) =>
                                    debouncedHandleUpdateQuantity(
                                      item.id,
                                      parseInt(e.target.value)
                                    )
                                  }
                                  min="1"
                                  max={item.stockQuantity}
                                  readOnly
                                />
                                <button
                                  type="button"
                                  className="quantity__plus border border-end border-gray-100 flex-shrink-0 h-32 w-32 text-neutral-600 flex-center bg-gray-100 hover-bg-main-600 hover-text-white"
                                  onClick={() =>
                                    debouncedHandleUpdateQuantity(
                                      item.id,
                                      item.quantity + 1
                                    )
                                  }
                                >
                                  <i className="ph ph-plus" />
                                </button>
                              </div>
                            </td>
                            <td>
                              <span className="text-sm  mb-0 fw-semibold">
                                LKR{" "}
                                {(item.quantity * effectivePrice).toFixed(2)}
                              </span>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="text-danger-600 btn btn-lg"
                                onClick={() => handleRemoveFromCart(item.id)}
                              >
                                <i className="ph ph-trash text-lg d-flex" />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center py-40">
                          No items in cart. Start shopping now....
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex-between flex-wrap gap-16 mt-10">
                {isAuthenticated && cart.length > 0 && (
                  <div className="flex-align gap-16">
                    {couponDetails === null ? (
                      <>
                        <input
                          type="text"
                          className="common-input-two"
                          placeholder="Coupon Code"
                          name="couponCode"
                          value={couponCode}
                          onChange={handleInputChange}
                        />
                        <button
                          type="button"
                          onClick={handleCouponSubmit}
                          className="btn btn-main btn-sm w-100 rounded-8"
                          disabled={couponCode === ""}
                        >
                          Apply Coupon
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="coupon-details text-sm">
                          <p>
                            <strong>Coupon Code:</strong>{" "}
                            {couponDetails.couponCode}
                          </p>
                          <p>
                            <strong>Description:</strong>{" "}
                            {couponDetails.description}
                          </p>
                          <p>
                            <strong>Discount:</strong>{" "}
                            {couponDetails.couponType === "PERCENTAGE"
                              ? `${couponDetails.discountedValue}%`
                              : `$${couponDetails.discountedValue}`}
                          </p>
                          <p>
                            <strong>Expires On:</strong>{" "}
                            {new Date(
                              couponDetails.expirationDate
                            ).toLocaleDateString()}
                          </p>
                        </div>
                        <button
                          type="button"
                          className="btn btn-main btn-sm w-100 rounded-8"
                          onClick={handleRemoveCoupon}
                        >
                          Remove Coupon
                        </button>
                      </>
                    )}
                  </div>
                )}

                {cart.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm d-flex align-items-center "
                    onClick={handleDeleteCart}
                    disabled={cart.length === 0}
                  >
                    <i className="ph ph-trash-simple text-sm me-2" />
                    Clear Cart
                  </button>
                )}
              </div>
              {/* Show available coupons for authenticated users & disable selected or applied coupon */}
              {isAuthenticated && availableCoupons.length > 0 && (
                <div className="coupon-list mt-10">
                  <h6 className="text-sm fw-semibold mb-4">
                    Available Coupons
                  </h6>
                  <div className="row">
                    {availableCoupons.map((coupon) => (
                      <div key={coupon.couponId} className="col-md-4 mb-3">
                        <button
                          type="button"
                          className="btn btn-outline-main w-100 py-3 rounded-3"
                          onClick={() => {
                            setCouponDetails(coupon);
                            localStorage.setItem(
                              "couponDetails",
                              JSON.stringify(coupon)
                            );
                          }}
                          disabled={
                            couponDetails &&
                            couponDetails.couponId === coupon.couponId
                          }
                        >
                          <div>
                            <p>
                              <strong>Code:</strong> {coupon.couponCode}
                            </p>
                            <p>
                              <strong>Discount:</strong>{" "}
                              {coupon.discountedValue}
                              {coupon.couponType === "PERCENTAGE" ? "%" : ""}
                            </p>
                            <p>
                              <strong>Description:</strong> {coupon.description}
                            </p>
                            <p>
                              <strong>Expires on:</strong>{" "}
                              {new Date(
                                coupon.expirationDate
                              ).toLocaleDateString()}
                            </p>
                          </div>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {cart.length > 0 && (
            <div className="col-xl-3 col-lg-4">
              <div className="cart-sidebar border border-gray-100 rounded-8 px-20 py-10">
                <p className="text-sm fw-bold h6 text-black text-center">Cart Total</p>
                <div className="bg-color-three rounded-8 p-24">
                  <div className="mb-32 flex-between gap-8">
                    <span className="text-gray-900 fw-semibold text-sm">
                      Subtotal
                    </span>
                    <span className="text-gray-900 fw-semibold text-sm">
                      LKR {cartTotal}
                    </span>
                  </div>
                  {couponDetails && (
                    <div className="mb-32 flex-between gap-8">
                      <span className="text-gray-900 fw-semibold text-sm">
                        Discount ({couponDetails.couponCode})
                      </span>
                      <span className="text-gray-900 fw-semibold text-sm">
                        -LKR {discountAmount.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="mb-20 flex-between gap-8">
                    <span className="text-gray-900 fw-semibold text-sm">
                      Delivery Fee
                    </span>
                    <span className="text-gray-900 fw-semibold text-sm">
                      LKR {deliveryFee.toFixed(2)}
                    </span>
                  </div>
                  {/* <div className="mb-0 flex-between gap-8">
                    <span className="text-gray-900 font-heading-two">
                      Estimated Tax
                    </span>
                    <span className="text-gray-900 fw-semibold">USD 10.00</span>
                  </div> */}
                </div>
                <div className="bg-color-three rounded-8 p-20 mt-20">
                  <div className="flex-between gap-8">
                    <span className="text-gray-900 text-sm fw-semibold">
                      Total
                    </span>
                    <span className="text-gray-900 text-sm fw-semibold">
                      LKR {finalTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
                <Link
                  to="/checkout"
                  className="btn btn-main mt-20  w-100 rounded-8 btn-sm text-sm"
                >
                  Proceed to checkout
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CartSection;
